import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { apiGet, apiGetBlob, apiPost } from '@/services/apiClient';
import type {
  CommentResponse,
  CommentsApiRequest,
  DashboardTab,
  EnableNotificationsRequest,
  GetFoliosResponse,
  NotificationResponse,
  ReservationGridPostRequest,
  ReservationGridResponse,
} from '@/features/dashboard/types';

export const reservationQueryKeys = {
  all: ['reservations'] as const,
  dashboard: (request: ReservationGridPostRequest) =>
    [...reservationQueryKeys.all, 'dashboard', request] as const,
  comments: (email: string, eventId: string, registrationId: string) =>
    [...reservationQueryKeys.all, 'comments', email, eventId, registrationId] as const,
  notifications: (registrationId: string, eventId: string) =>
    [...reservationQueryKeys.all, 'notifications', registrationId, eventId] as const,
  folios: (hmsConfirmationNum: string) =>
    [...reservationQueryKeys.all, 'folios', hmsConfirmationNum] as const,
};

export function useReservationsDashboardQuery(request: ReservationGridPostRequest) {
  return useQuery({
    queryKey: reservationQueryKeys.dashboard(request),
    queryFn: () =>
      apiPost<ReservationGridPostRequest, ReservationGridResponse>(
        'Reservations/GetPaginatedLongTermReservations',
        request,
      ),
    enabled: !request.searchText || request.searchText.length >= 3,
  });
}

export function useEnableNotificationsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: EnableNotificationsRequest) =>
      apiPost<EnableNotificationsRequest, boolean>('Reservations/EnableNotifications', request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reservationQueryKeys.all }),
  });
}

export function useSendCommentsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CommentsApiRequest[]) =>
      apiPost<CommentsApiRequest[], boolean>('Reservations/SendComments', request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reservationQueryKeys.all }),
  });
}

export function useGuestCommentsQuery({
  email,
  eventId,
  registrationId,
}: {
  email: string;
  eventId: string;
  registrationId: string;
}) {
  return useQuery({
    queryKey: reservationQueryKeys.comments(email, eventId, registrationId),
    queryFn: () =>
      apiGet<CommentResponse[]>(
        `Reservations/GetComments/${encodeURIComponent(eventId)}/${encodeURIComponent(registrationId)}?userEmailId=${encodeURIComponent(email)}`,
      ),
    enabled: Boolean(email && eventId && registrationId),
  });
}

export function useGuestNotificationsQuery(registrationId: string, eventId: string) {
  return useQuery({
    queryKey: reservationQueryKeys.notifications(registrationId, eventId),
    queryFn: () =>
      apiGet<NotificationResponse[]>(
        `Reservations/GetNotifications/${encodeURIComponent(registrationId)}/${encodeURIComponent(eventId)}`,
      ),
    enabled: Boolean(registrationId && eventId),
  });
}

export function useSendNotificationsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ notifyUsers, loggedInUserEmail }: { notifyUsers: string; loggedInUserEmail: string }) =>
      apiGet<boolean>(
        `Reservations/SendNotifications/${encodeURIComponent(notifyUsers)}/${encodeURIComponent(loggedInUserEmail)}`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reservationQueryKeys.all }),
  });
}

export function useGetFoliosQuery(hmsConfirmationNum: string | null | undefined) {
  return useQuery({
    queryKey: reservationQueryKeys.folios(hmsConfirmationNum ?? ''),
    queryFn: () =>
      apiGet<GetFoliosResponse>(
        `Reservations/GetFolios?hMSConfirmationNum=${encodeURIComponent(hmsConfirmationNum!)}`,
      ),
    enabled: Boolean(hmsConfirmationNum),
  });
}

export function useGetFoliosQueries(hmsNums: (string | null | undefined)[]) {
  return useQueries({
    queries: hmsNums.map((hmsNum) => ({
      queryKey: reservationQueryKeys.folios(hmsNum ?? ''),
      queryFn: () =>
        apiGet<GetFoliosResponse>(
          `Reservations/GetFolios?hMSConfirmationNum=${encodeURIComponent(hmsNum!)}`,
        ),
      enabled: Boolean(hmsNum),
    })),
  });
}

export function exportTravelDashboard(
  startDate: Date,
  endDate: Date,
  issueType: DashboardTab,
  searchText: string,
): Promise<Blob> {
  const segments = [
    'Reservations/ExportTravelDashboard',
    format(startDate, 'yyyy-MM-dd'),
    format(endDate, 'yyyy-MM-dd'),
    encodeURIComponent(issueType),
  ];
  const trimmedSearchText = searchText.trim();
  if (trimmedSearchText) segments.push(encodeURIComponent(trimmedSearchText));

  return apiGetBlob(segments.join('/'));
}